import PublicShell from "@/components/PublicShell";
import { site } from "@/lib/api";
import { useAsync } from "@/lib/hooks";

const UPDATED = "9 September 2026";

/**
 * Plain English, and only claims the code actually keeps. Every sentence here
 * names a table, a setting or a call that exists; when one of those changes,
 * this page changes with it.
 */
export default function Privacy() {
  const info = useAsync(() => site.info().catch(() => null), []);
  const contact = info.data?.contact_email;

  return (
    <PublicShell contactEmail={contact}>
      <article className="prose-pool max-w-2xl">
        <p className="unit">Privacy policy · updated {UPDATED}</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">What Pool Nerd stores, and why</h1>

        <Section title="Who runs this">
          Pool Nerd is a small, independently run service.
          {contact ? (
            <>
              {" "}
              You can reach the person who runs it at{" "}
              <a className="underline" href={`mailto:${contact}`}>
                {contact}
              </a>
              .
            </>
          ) : (
            " The contact address is shown on the sign-in page."
          )}
        </Section>

        <Section title="Your account">
          You sign in with Google, Microsoft, Facebook or GitHub. Pool Nerd receives your email
          address, your name, a profile picture link if the provider offers one, and the
          provider's own identifier for you. It never sees your password. If the provider confirms
          that it has verified your email address, a later sign-in with a different provider using
          the same verified address joins the same account rather than creating a second one.
        </Section>

        <Section title="Your pool">
          Everything you log is stored so it can be shown back to you: water test results, chemical
          doses, events like rain or a drain, equipment and pump schedules, notes, and any photos
          you upload. Photos are re-encoded on upload and location data is removed from them.
          Uploaded photos are stored in a private bucket and are only ever served to a signed-in
          member of the household that uploaded them.
        </Section>

        <Section title="Your location">
          If you enter a postal code, it is resolved once to coordinates and a time zone, which are
          stored with the pool. They are used to look up daily rainfall and temperature from
          Open-Meteo (a public weather service) and to work out sunrise and sunset for chlorine
          advice. Coordinates are rounded to about a kilometre before the weather lookup. Without a
          postal code, no location is stored and those features are simply off.
        </Section>

        <Section title="The assistant">
          When you ask the assistant for advice, or ask it a question, a summary of your recent
          readings, doses and events is sent to Anthropic's Claude API to produce the answer. The
          request is made with Pool Nerd's own API key, under Anthropic's commercial terms, which
          do not permit training on the data. The question and the answer are stored in your
          account so you can read them again. Pool Nerd never sends anything to the assistant
          unless you tap the button that asks it to.
        </Section>

        <Section title="Cookies">
          One cookie keeps you signed in for up to thirty days. A second, short-lived cookie
          exists only for the few minutes it takes to complete a sign-in. There is no analytics,
          advertising or tracking of any kind, and no third-party scripts run on the page.
        </Section>

        <Section title="Sign-in records">
          Pool Nerd keeps a record of sign-ins and sign-in attempts for ninety days: when, through
          which provider, and whether it succeeded. If you try to sign up while the site is not
          open to new accounts, the email address you tried with is kept for the same ninety days
          so the site's operator can invite you. These records are seen only by the operator and
          are deleted on schedule.
        </Section>

        <Section title="Where it lives">
          Data is stored on Amazon Web Services in the United States, encrypted at rest, with
          automated backups kept for a limited period so the service can recover from a fault.
        </Section>

        <Section title="Deleting your data">
          Settings has a "Delete my account" control. It removes your account and, if you were the
          last member of the household, every reading, dose, event, photo and conversation in it.
          The deletion is immediate; copies in backups age out on their own schedule.
        </Section>

        <Section title="Changes">
          If this policy changes in a way that matters, the date at the top will change and the
          sign-in page will say so.
        </Section>
      </article>
    </PublicShell>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-7">
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="mt-2 text-sm leading-relaxed text-(--color-ink-muted)">{children}</p>
    </section>
  );
}
